<template>
  <div class="pa-storybook-wrapper">
    <transition
     name="storybook-cover"
     @beforeEnter="handleBeforeEnter"
     @afterEnter="handleAfterEnter"
     @afterLeave="handleAfterLeave"
    >
      <div class="pa-storybook-cover" v-if="isOpenContent">
        <div class="pa-storybook" ref="storyBook">
          <StoryBookLoading   :isOpen="isLoading" />
          <StoryBookTitle     :isOpen="isOpenTitle" />
          <StoryBookNavigator :isOpen="isOpenNavigator" />
          <StoryBookPage      :isOpen="isOpenPage" />
        </div>
      </div>
    </transition>

    <transition name="storybook-overlay" @enter="handleOverlayEnter" @afterEnter="handleOverlayAfterEnter">
      <div class="pa-storybook-overlay" v-if="isOpenOverlay"></div>
    </transition>
  </div>
</template>

<script>
import safeAreaInsets from 'safe-area-insets'

import StoryBookLoading   from '@/components/Parts/StoryBook/StoryBookLoading.vue'
import StoryBookTitle     from '@/components/Parts/StoryBook/StoryBookTitle.vue'
import StoryBookNavigator from '@/components/Parts/StoryBook/StoryBookNavigator.vue'
import StoryBookPage      from '@/components/Parts/StoryBook/StoryBookPage.vue'

import {GetStoryBookmark, GetChapterData, GetChapterPages} from '@/graphql/queries/Story.graphql'

import {GetCharacterMeta} from '@/graphql/queries/Character.graphql'

export default {
  components : {
    StoryBookLoading,
    StoryBookTitle,
    StoryBookNavigator,
    StoryBookPage
  },

  data(){
    return {
      isOpenContent       : false,
      isOpenOverlay       : false,

      couldReadStoryMetaData  : false,
    };
  },

  apollo : {
    bookmark : {
      query : GetStoryBookmark,
      variables(){
        return {
          storyId : this.selectedStoryId,
        };
      },
      skip(){
        return !this.couldReadStoryMetaData;
      },
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    chapterData : {
      query : GetChapterData,
      variables(){
        return {
          id  : this.selectedChapterId
        }
      },
      skip(){
        return !this.couldReadStoryMetaData;
      },
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    chapterPages : {
      query : GetChapterPages,
      variables(){
        return {
          storyChapterId  : this.selectedChapterId
        }
      },
      skip(){
        return !this.couldReadStoryMetaData;
      },
      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      }
    },

    characterMetaList : {
      query : GetCharacterMeta
    }
  },

  computed : {
    displayHeight(){
      return window.innerHeight;
    },

    isOpen(){
      return this.$store.state.storyBook.isOpenStoryModal;
    },

    isLoading(){
      return this.$store.state.storyBook.isOpenStoryModalLoading;
    },

    isOpenPage(){
      return this.$store.state.storyBook.isOpenStoryModalPage;
    },

    isOpenNavigator(){
      return this.$store.state.storyBook.isOpenStoryModalNavigator;
    },

    isOpenTitle(){
      return this.$store.state.storyBook.isOpenStoryTitle;
    },

    selectedStoryId(){
      return this.$store.state.storyBook.selectedStoryId;
    },

    selectedChapterId(){
      return this.$store.state.storyBook.selectedChapterId;
    },
  },

  watch : {
    isOpen : {
      handler(value){
        if(value === true){
          this.isOpenOverlay = true;
        }else{
          this.isOpenContent = false;
        }
      },
      immediate: true
    }
  },

  methods : {
    waitMetaDataLoading(minimumWaitTime = 5000, maxWaitTime = 10000, interval = 500){
      return new Promise((resolve) => {
        let times = 0;

        let clearMiniumLoadingTime = false;

        setTimeout(() => {
          clearMiniumLoadingTime = true;
        }, minimumWaitTime);

        const intervalId = setInterval(() => {
          times++;

          if(clearMiniumLoadingTime === false) return;

          const completeLoading = function(){
            clearInterval(intervalId);

            resolve();
          };

          if(
            (this.$apollo.queries.bookmark.loading === false) && 
            (this.$apollo.queries.chapterData.loading === false) &&
            (this.$apollo.queries.chapterPages.loading === false)
          ){
            completeLoading();
          }

          if(times >= (maxWaitTime / interval)){
            // console.log('Has Story Meta Load Error.');
            completeLoading();
          }
        }, interval);
      });
    },

    setSafeArea(){
      if(typeof this.$refs.storyBook === 'undefined') return;

      this.$refs.storyBook.style.height = ( this.displayHeight - safeAreaInsets.bottom - (8*2) ) + 'px';
    },

    handleBeforeEnter(){
      this.setSafeArea();
    },

    async handleAfterEnter(){
      // モーダルが表示されてから、bodyのスクロールをロックします。
      this.$store.dispatch({
        type  : 'layouts/updateIsBodyNoScroll',
        value : true
      });

      // Loadingを表示します。
      this.$store.dispatch({
        type  : 'storyBook/updateIsOpenStoryModalLoading',
        value : true
      });

      // キャラクターのブックマーク、チャプターデータ、ページデータを取得
      this.couldReadStoryMetaData = true;
      await this.waitMetaDataLoading();

      // Loadingを終了します。
      this.$store.dispatch({
        type  : 'storyBook/updateIsOpenStoryModalLoading',
        value : false
      });

      // StoryPageを表示します。
      this.$store.dispatch({
        type  : 'storyBook/updateIsOpenStoryModalPage',
        value : true
      });

      // StoryNavigatorを表示します。
      this.$store.dispatch({
        type  : 'storyBook/updateIsOpenStoryModalNavigator',
        value : true
      });
    },

    handleAfterLeave(){
      this.isOpenOverlay = false;

      // bodyスクロールのロックを解除します。
      this.$store.dispatch({
        type  : 'layouts/updateIsBodyNoScroll',
        value : false
      });

      // StoryPageを非表示にします。
      this.$store.dispatch({
        type  : 'storyBook/updateIsOpenStoryModalPage',
        value : false
      });

      // StoryNavigatorを非表示にします。
      this.$store.dispatch({
        type  : 'storyBook/updateIsOpenStoryModalNavigator',
        value : false
      });

      setTimeout(() => {
        this.$apollo.queries.characterMetaList.refetch();
      },3000);
    },

    handleOverlayEnter(){
      setTimeout(() => {
        this.isOpenContent = true;
      }, 500);
    },

    handleOverlayAfterEnter(){
      // this.isOpenContent = true;
    }
  }
}
</script>

<style lang="scss">
@import "~bulma/sass/utilities/_all";
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/mixins.scss";

@keyframes simpleFadeIn
{
  0%   {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes storybookFadeAndBlur
{
  0%   {
    opacity:0;
    backdrop-filter: blur(0px);
  }
  100% {
    opacity:.9;
    backdrop-filter: blur(3px);
  }
}

.storybook-cover{
  &-enter-active{
    animation: simpleFadeIn 1s;
  }

  &-leave-active{
    animation: simpleFadeIn .5s reverse;
  }

  &-enter, &-leave-to{
    opacity: 0;
  }
}

.storybook-overlay{
  &-enter-active{
    animation: storybookFadeAndBlur .8s;
  }

  &-leave-active {
    animation: storybookFadeAndBlur 1s reverse;
  }

  &-enter, &-leave-to{
    opacity: 0;
  }
}

.pa-storybook-wrapper{
  box-sizing: border-box;

  .pa-storybook-cover{
    position: fixed;
    top:0;
    left:0;
    z-index: 1000;
    width: 100vw;
    height: 100vh;

    .pa-storybook{
      @include pa-golden-box(4px, 8px, 0);
      font-size:.8rem;
      line-height: 1.3rem;
      text-align: center;

      margin:8px 5px;

      // ここで指定している16pxは上下につける標準marginです。
      // ２つ目の16pxはpa-golden-boxのpadding用です。
      height: calc(100vh - (env(safe-area-inset-top) + env(safe-area-inset-bottom) + 16px + 16px));

      &::after {
        @include pa-golden-box-after(4px, 8px);
      }

      @media screen and (min-width: $desktop) {
        width:720px;
        margin:12px auto 18px;
        font-size:1.1rem;
        line-height: 1.4rem;
        height: calc(100vh - (env(safe-area-inset-top) + env(safe-area-inset-bottom) + 16px + 16px + 12px + 18px));
      }
    }
  }

  .pa-storybook-overlay{
    position: fixed;
    top:0;
    left:0;
    z-index: 999;
    width: 100vw;
    height: 100vh;
    background:linear-gradient(0deg, rgba($body-background-primary, .9), rgba($body-background-semi-light-darker, .9));
    backdrop-filter: blur(3px) opacity(.9);
  }
}
</style>